import { ComposerContext } from './ComposerContext';
import { RECIPIENT_TYPES } from 'reducers/roleBasedMessagingSearch';
import { MODAL_FILE_CONTEXT_KEY } from 'reducers/fileUploads';
// @ts-ignore
import SelectRecipientPanel, { SelectRecipientTabs } from './SelectRecipientPanel';

import { Backbone$Id } from 'types/typescript/Backbone';
import CenteredLoadingIndicator from 'components/CenteredLoadingIndicator';
import ChatStore from 'chat/stores/ChatStore';
import Chats from 'collections/Chats';
// @ts-ignore
import { DragDropContext } from 'react-dnd';
import HTML5Backend from 'react-dnd-html5-backend';
import { OrderedMap } from 'immutable';
import PotentialChatMember from 'entities/PotentialChatMember';
import React from 'react';
// @ts-ignore
import SplitComposerHeader from './SplitComposerHeader';
import { Target } from 'entities/Message';
import { UploadRequestData } from 'reducers/fileUploads';
import { isEqual } from 'lodash';
import SearchTimer from './SearchTimer';
import CurrentUser from 'auth/CurrentUser';

import { Recipient, ComposerGroup, ComposerOrganization } from 'types/typescript/messagingTypes';

export type MessageType = 'urgent' | 'message';

type GQLProps = {
  groupsLoading: boolean;
  groups?: ComposerGroup[] | null;

  // graphQL
  orgsLoading: boolean;
  organizations?: ComposerOrganization[];
  schools?: ComposerOrganization[];
};

export type ReduxDispatchProps = {
  clearSelection: () => void;
  removeFile: (fileContextKey: string, filename: string) => void;
  onSelectInitialTargets: (targets: Target[]) => void;
  onLoadOrganizations: (organizations: ComposerOrganization[]) => void;
  onLoadGroups: (groups: ComposerGroup[]) => void;
  onLoadGroupChats: (chats: Chats) => void;
  onLoadIndividuals: (members: PotentialChatMember[]) => void;
  onLoadPotentialChatMembers: (query: string) => void;
  onSearchGroupChats: (query: string) => void;
};

export type ReduxStateProps = {
  savedMessage: string;
  fileUploads: OrderedMap<string, UploadRequestData>;
  selection: Recipient[];

  loadingGroupChats: boolean;
  loadingIndividualRecipients: boolean;
};

export type GeneratedProps = GQLProps & ReduxDispatchProps & ReduxStateProps;
export type RequiredProps = {
  defaultTab?: SelectRecipientTabs;
  initialTargets?: Target[];
  onSignificantChangeMade?: () => void;
  onDismiss?: () => void;
  skipSelectRecipients: boolean;
  onPageChange?: () => void;
  messageType: MessageType;
  shareSdkBodyUrl?: string;
};
type Props = RequiredProps & GeneratedProps;

type State = {
  studentIdListCreated: boolean;
};

class SplitComposer extends React.Component<Props, State> {
  static defaultProps = {
    messageType: 'message',
  };

  constructor(props: Props) {
    super(props);
    const studentIdListRecipientInSelection = props.selection.some(
      s => s.type === RECIPIENT_TYPES.STUDENT_ID_LIST_RECIPIENT
    );

    this.state = {
      studentIdListCreated: studentIdListRecipientInSelection,
    };
  }

  componentDidMount() {
    const {
      initialTargets,
      onLoadOrganizations,
      onLoadGroups,
      onLoadGroupChats,
      onLoadIndividuals,
      onSearchGroupChats,
      onLoadPotentialChatMembers,
      organizations,
      groups,
    } = this.props;

    if (organizations) {
      onLoadOrganizations(organizations);
    }

    if (groups) {
      onLoadGroups(groups);
    }

    // Make sure we show available group chats.
    const groupChats = ChatStore.getGroupChats();
    if (groupChats.length === 0) {
      onSearchGroupChats('');
    } else {
      onLoadGroupChats(groupChats);
    }

    // Make sure we have some people to show.
    const individuals = ChatStore.getEligibleMemberModels();
    if (individuals.length === 0) {
      onLoadPotentialChatMembers('');
    } else {
      onLoadIndividuals(individuals);
    }

    if (initialTargets) {
      this.props.onSelectInitialTargets(initialTargets);
    }
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    const groupsWereNotPreviouslyLoaded = !prevProps.groups;
    const orgsWereNotPreviouslyLoaded = !prevProps.organizations;
    const prevGroupUuids = prevProps.groups ? prevProps.groups.map(group => group.uuid) : [];
    const newGroupUuids = this.props.groups ? this.props.groups.map(group => group.uuid) : [];
    const newGroupsWereLoaded = !isEqual(prevGroupUuids, newGroupUuids);
    const prevOrgUuids = prevProps.organizations
      ? prevProps.organizations.map(org => org.uuid)
      : [];
    const newOrgUuids = this.props.organizations
      ? this.props.organizations.map(org => org.uuid)
      : [];
    const newOrgsWereLoaded = !isEqual(prevOrgUuids, newOrgUuids);
    const { initialTargets } = this.props;

    if (this.props.groups && (groupsWereNotPreviouslyLoaded || newGroupsWereLoaded)) {
      this.props.onLoadGroups(this.props.groups);
      if (initialTargets) {
        this.props.onSelectInitialTargets(initialTargets);
      }
    }

    if (this.props.organizations && (orgsWereNotPreviouslyLoaded || newOrgsWereLoaded)) {
      this.props.onLoadOrganizations(this.props.organizations);
      if (initialTargets) {
        this.props.onSelectInitialTargets(initialTargets);
      }
    }

    const changed =
      prevState.studentIdListCreated !== this.state.studentIdListCreated ||
      (prevProps.selection.length !== this.props.selection.length && this.props.selection.length);

    if (changed) {
      if (this.props.onSignificantChangeMade) {
        this.props.onSignificantChangeMade();
      }
    }

    if (!this.groupResultsAreLoading() && this.props.skipSelectRecipients) {
      this.advanceToComposeMessage();
    }
  }

  groupResultsAreLoading = () => {
    const { orgsLoading, groupsLoading, loadingGroupChats } = this.props;
    return orgsLoading || groupsLoading || loadingGroupChats;
  };

  componentWillUnmount() {
    this.removeAllFiles();
  }

  advanceToComposeMessage = () => {
    if (this.props.onPageChange) {
      this.props.onPageChange();
    }
  };

  handleSuccessfulStudentIdListCreation = () => {
    this.setState({ studentIdListCreated: true });
  };

  removeAllFiles = () => {
    const { fileUploads } = this.props;
    fileUploads
      .keySeq()
      .forEach(filename => filename && this.props.removeFile(MODAL_FILE_CONTEXT_KEY, filename));
  };

  render() {
    const {
      selection,
      initialTargets,
      orgsLoading,
      schools,
      groupsLoading,
      loadingGroupChats,
      loadingIndividualRecipients,
      messageType,
      skipSelectRecipients,
    } = this.props;

    const isUrgentMessage = messageType === 'urgent';

    const initialTarget = initialTargets && initialTargets[0];
    const groupTargetId: Backbone$Id | null | undefined =
      initialTarget && initialTarget.type === 'group_target' ? initialTarget.group_id : null;
    const orgTargetId: Backbone$Id | null | undefined =
      initialTarget && initialTarget.type === 'district_target' ? initialTarget.org_id : null;

    const hasSearchTimer = CurrentUser.getUser().hasFeature('composer_dd_trace.web');

    if (skipSelectRecipients) {
      return <CenteredLoadingIndicator />;
    }

    return (
      <React.Fragment>
        <ComposerContext.Provider
          value={{
            isUrgentMessage,
            messageType: 'message', // note this is default and should be changed here in the future
            // eslint-disable-next-line object-shorthand
            getUIEventContext: function() {
              return {
                message_type: this.messageType,
                is_urgent: this.isUrgentMessage,
                composer_version: 'web_full_page',
              };
            },
          }}>
          <SplitComposerHeader messageType={messageType} />
          <SelectRecipientPanel
            onContinue={this.advanceToComposeMessage}
            loadingGroupResults={this.groupResultsAreLoading()}
            loadingPeopleResults={loadingIndividualRecipients}
            schools={schools}
            selection={selection}
            onTabChange={this.props.clearSelection}
            onSuccessfulStudentIdListCreation={this.handleSuccessfulStudentIdListCreation}
            defaultTab={this.props.defaultTab}
            initialTargetId={(groupTargetId || orgTargetId) ?? undefined}
            messageType={messageType}
          />
        </ComposerContext.Provider>
        {hasSearchTimer && (
          <SearchTimer
            orgsLoading={orgsLoading}
            groupsLoading={groupsLoading}
            groupChatsLoading={loadingGroupChats}
            individualRecipientsLoading={loadingIndividualRecipients}
            selection={selection}
            page={1}
            messageSent={false}
          />
        )}
      </React.Fragment>
    );
  }
}

// we export this for the SplitComposerModal integration test
// because TargetByStudentIdUploadPage expects a DragDropContext
const SplitComposerWithDnD = DragDropContext(HTML5Backend)(SplitComposer);

export { SplitComposer, SplitComposerWithDnD };

export default SplitComposer;
